import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import FilterModal from "@/components/yts/FilterModal";
import { CATEGORIES, catMatches, subMatches, zoneMatches, neighborhoodRank, CHINCHORROS_KIOSKS } from "@/data/taxonomy";
import { inGuideOrder, listingToBeach } from "@/data/beaches";
import { normalizeViator, mixSources, rankPartner, isRideService, asRideService } from "@/lib/viator";
import { listingPath } from "@/lib/businessSlug";
import { shortType } from "@/lib/businessType";
import { distanceKm } from "@/lib/coords";
import { withNetworkRetry } from "@/lib/requestRetry";

const noop = (..._args) => {};
// Editorially removed supplier items must stay out of public discovery even if
// a future FareHarbor import creates or re-publishes the same item.
const REMOVED_FAREHARBOR_ITEMS = new Set(["484249", "551117"]);
const excludedSupplierListing = (item) => {
  if (item?.affiliate_provider !== "FareHarbor") return false;
  const source = String(item.booking_url || item.primary_action_url || "");
  const id = source.match(/\/items\/(\d+)/)?.[1];
  return REMOVED_FAREHARBOR_ITEMS.has(id);
};
const emptySet = () => new Set();
const withTimeout = (promise, ms, label) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
]);
// Every page-load query goes through here: a dropped connection is retried
// once, so one blip no longer leaves the guide half-loaded or logs an error.
// A real answer from the server is never repeated.
const request = (make, ms, label) => withNetworkRetry(() => withTimeout(make(), ms, label));
// Make search tolerant of accents and punctuation (Rio Piedras / Río Piedras).
const normalizeSearchText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, " ")
  .trim();
// Safe default so HMR remounts never read null (root cause of the
// "Cannot destructure property of useExplore(...) as it is null" errors).
const SAFE_EXPLORE = {
  allListings: [], beaches: [], viatorListings: [], tourExperiences: [], rideServices: [], badBunnyListings: [], neighborhoods: [], categories: [], chips: ["Explore"], loading: true, loadError: false, nbNames: {},
  query: "", setQuery: noop, activeChip: "Explore", setActiveChip: noop,
  experienceBucket: "All", setExperienceBucket: noop,
  filterCats: emptySet(), setFilterCats: noop, filterSubs: emptySet(), setFilterSubs: noop,
  filterNb: emptySet(), setFilterNb: noop,
  clearFilters: noop, filteredListings: [], hasFilter: false,
  nearMe: false, setNearMe: noop, nearCoords: null, locating: false, nearError: "", toggleNearMe: noop,
  searchOpen: false, setSearchOpen: noop, filterOpen: false, setFilterOpen: noop,
  drawerOpen: false, setDrawerOpen: noop, selectChip: noop, selectNeighborhood: noop, toggleSub: noop,
};
const ExploreContext = createContext(SAFE_EXPLORE);

// Treat a cover, storefront or gallery image as usable media when ranking cards.
export const hasListingImage = (listing) =>
  Boolean(
    listing?.cover_image ||
    listing?.storefront_image ||
    listing?.image ||
    listing?.image_url ||
    (Array.isArray(listing?.gallery_images) && listing.gallery_images.some(Boolean))
  );

// Preserve the current editorial order within each group, but keep photo-ready
// listings ahead of text-only listings in browse results.
export const photosFirst = (items) =>
  [...(items || [])].sort((a, b) => Number(hasListingImage(b)) - Number(hasListingImage(a)));

// `nbNames` maps a listing's neighborhood_id to the neighborhood's display name,
// so cards can show a neighborhood instead of a street address.
export const toCard = (l, nbNames = {}) => ({
  id: l.id,
  source: l.source,
  product_code: l.product_code,
  affiliate_provider: l.affiliate_provider,
  booking_url: l.booking_url,
  primary_action_url: l.primary_action_url,
  title_es: l.title_es,
  price_from: l.price_from,
  description_es: l.description_es,
  editorial_description_es: l.editorial_description_es,
  short_description: l.short_description,
  short_description_es: l.short_description_es,
  // A card states the type in plain words ("Bakery"), not the stored category.
  label: shortType(l),
  title: l.name,
  meta: l.editorial_description || l.description || l.short_description || "",
  price: l.price_label,
  // A single curated photo goes into browse cards. Gallery arrays stay on the
  // original listing record for its detail page and never become card slides.
  image: l.cover_image || l.storefront_image || l.image || l.image_url ||
    (Array.isArray(l.gallery_images) ? l.gallery_images.find(Boolean) : "") ||
    (Array.isArray(l.images) ? l.images.find(Boolean) : "") || "",
  // Prefer the neighborhood record, then the listing's own location fields.
  // Local listings often store their area directly instead of a neighborhood id.
  location: nbNames[l.neighborhood_id] || (l.affiliate_provider === "FareHarbor" ? String(l.address || "").split(",")[0] : "") || l.neighborhood || l.city || l.destination || l.beach_area || "",
  rating: l.rating,
  review_count: l.review_count,
  // Card-level sorting (newest first) needs the day the place joined the guide.
  created_date: l.created_date,
  verified: l.verified,
  editor_pick: l.editor_pick,
  locals_know: l.locals_know,
  // The week travels with the card so it can say whether the place is open at
  // this moment — partner trips carry none, and so show no status.
  hours_json: l.hours_json,
  hours_text: l.hours_text,
  // A service the guide has opened but nobody has claimed yet: the card says so
  // instead of pretending there is a business behind it.
  coming_soon: l.coming_soon,
  // A local business is addressed by its own name (/Hipopótamo). Bookable
  // partner trips have no such page, so only local cards carry a path.
  path: l.source ? "" : listingPath(l),
});

// Category + subcategory resolution lives in the taxonomy module; re-exported
// here so every existing importer keeps working.
export { catMatches, subMatches };

// Local experiences lead; when the directory is thin the rail keeps filling
// from the bookable Viator catalog.
export const railExperiences = (listings, viator = []) => {
  const r = listings.filter((l) => catMatches(l, "Experiences"));
  if (!viator.length || r.length >= 14) return r.length ? r : listings;
  const merged = [...r];
  viator.forEach((item) => {
    if (merged.length < 14) merged.push(item);
  });
  return merged;
};

export const railPointsOfInterest = (listings) => {
  const r = listings.filter((l) => catMatches(l, "Culture"));
  return r.length ? r : listings;
};

export const railEatDrink = (listings) => {
  // The chinchorros carry their own rail further up the page, so they are not
  // served a second time here.
  const r = listings.filter((l) => catMatches(l, "Gastronomy") && !subMatches(l, CHINCHORROS_KIOSKS));
  return r.length ? r : listings;
};

// The island's own casual, counter-service eating gets its own carousel, so the
// places travelers come here for are visible up front.
export const railChinchorros = (listings) => listings.filter((l) => subMatches(l, CHINCHORROS_KIOSKS));

// What "off the beaten path" means in a place's own words. Shared with the
// landing rail, which reads the partner catalog with the same vocabulary.
export const OFF_BEATEN_TERMS = [
  "hidden",
  "secret",
  "lookout",
  "coastal",
  "viewpoint",
  "beaten path",
  "gem",
  "secluded",
  "off the grid",
  "quiet escape",
];

// Off the beaten path is a claim about a place, not a bucket for whatever is
// left over: only listings whose own words say so appear here. An empty rail
// then steps aside rather than repeating the rest of the page.
export const railOffBeatenPath = (listings) => {
  const hay = (l) =>
    `${l.name} ${l.category} ${l.subcategory || ""} ${l.editorial_description || ""} ${l.description || ""}`.toLowerCase();
  return listings.filter((l) => OFF_BEATEN_TERMS.some((term) => hay(l).includes(term)));
};

export const railRooftops = (listings) => {
  const terms = ["rooftop", "roof", "lounge", "cocktail", "skybar", "sky bar", "terrace", "view", "sunset", "panoram"];
  const match = (l) => {
    const hay = `${l.name} ${l.category} ${l.subcategory || ""} ${l.editorial_description || ""} ${l.description || ""}`.toLowerCase();
    return terms.some((t) => hay.includes(t));
  };
  const tagged = listings.filter(match);
  if (tagged.length) return tagged;
  const bars = listings.filter((l) => {
    const t = (l.type || "").toLowerCase();
    const c = (l.category || "").toLowerCase();
    return t === "nightlife" || c.includes("bar") || c.includes("lounge") || c.includes("cocktail");
  });
  return bars.length ? bars : listings;
};

// Trending runs in the order the site's own editors set: a place given a
// position number leads in that order, and anything left unnumbered follows in
// the order it arrived.
export const railTrending = (listings) => {
  const featured = listings.filter((l) => l.featured);
  if (!featured.length) return listings.slice(0, 8);
  const ranked = featured.filter((l) => Number.isFinite(l.trending_rank));
  const rest = featured.filter((l) => !Number.isFinite(l.trending_rank));
  ranked.sort((a, b) => a.trending_rank - b.trending_rank || Number(hasListingImage(b)) - Number(hasListingImage(a)));
  return [...photosFirst(ranked), ...photosFirst(rest)];
};

// Locals picks is what the neighbourhoods themselves vouch for. The featured
// few already lead the page in Trending and the chinchorros have their own rail
// above, so neither is shown again here.
export const railLocals = (listings) => {
  const r = listings.filter(
    (l) => (l.locals_know || l.editor_pick) && !l.featured && !subMatches(l, CHINCHORROS_KIOSKS)
  );
  return r.length ? r : listings;
};

export function ExploreProvider({ children }) {
  const [allListings, setAllListings] = useState([]);
  // The beach guide is made of listings too, but it browses on its own — its own
  // rail, its own category, its own pins — so it is held apart from the catalog.
  const [beaches, setBeaches] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viatorListings, setViatorListings] = useState([]);
  // Airport transfers and private drivers sit deep in the partner catalog, far
  // past the top-ranked trips, so they are fetched on their own terms.
  const [transferCatalog, setTransferCatalog] = useState([]);
  const [badBunnyListings, setBadBunnyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [activeChip, setActiveChip] = useState("Explore");
  // A curated experience theme is selected by the compact "Type" dropdown.
  const [experienceBucket, setExperienceBucket] = useState("All");
  const [filterCats, setFilterCats] = useState(new Set());
  const [filterSubs, setFilterSubs] = useState(new Set());
  const [filterNb, setFilterNb] = useState(new Set());
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // "Near Me": where the visitor is once they have said so, and what the browse
  // is doing about it. The position is kept when the sort is dropped, so
  // switching it back on is instant and never asks the browser twice.
  const [nearCoords, setNearCoords] = useState(null);
  const [nearMe, setNearMe] = useState(false);
  const [locating, setLocating] = useState(false);
  const [nearError, setNearError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Keep shareable search URLs in sync with the browse state. This also makes
  // the homepage SearchAction useful when a visitor arrives from Google.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get("q") || "";
    setQuery((current) => current === urlQuery ? current : urlQuery);
  }, [location.search]);

  useEffect(() => {
    let alive = true;

    // Load supporting navigation data in parallel, but don't make the main
    // directory wait for neighborhoods or category metadata to finish.
    request(() => base44.entities.Neighborhood.list("-created_date", 100), 12000, "Neighborhood query")
      .then((nb) => {
        if (!alive) return;
        setNeighborhoods(
          [...(nb || [])].sort((a, b) => neighborhoodRank(a) - neighborhoodRank(b)).map((n) => ({
            id: n.id,
            name: n.name,
            desc: n.description,
            image: n.cover_image,
            // Neighborhood records carry no slug of their own; listings point at
            // the record id, so that is the key used for filtering and links.
            slug: n.slug || n.id,
            latitude: n.latitude,
            longitude: n.longitude,
          }))
        );
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });

    request(() => base44.entities.Category.list("sort_order", 100), 12000, "Category query")
      .then((cats) => {
        if (alive) setCategories(cats || []);
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });

    (async () => {
      try {
        // The directory is the homepage's critical data: render it as soon as
        // it arrives, without waiting for secondary requests.
        const list = await request(
          () => base44.entities.Listing.list("-created_date", 350),
          12000,
          "Directory query"
        );
        if (!alive) return;

        // Draft and archived listings are hidden from the public site — the admin
        // listing manager toggles that state with the eye button.
        const visible = (list || []).filter((l) => l.status !== "Draft" && l.status !== "Archived" && !excludedSupplierListing(l));
        setBeaches(inGuideOrder(visible.filter((l) => l.type === "Beach").map(listingToBeach)));
        // Listings with photos lead the site while the catalog is still growing;
        // within each group the existing newest-first order is preserved.
        setAllListings(
          visible
            .filter((l) => l.type !== "Beach")
            .sort((a, b) => Number(hasListingImage(b)) - Number(hasListingImage(a)))
        );
        setLoading(false);

        // Partner catalogs are optional enhancements; failures should not block
        // the local guide or turn a successful directory response into a blank page.
        Promise.allSettled([
          // The first page renders immediately. Fetch the remaining published
          // experiences in the background so the full catalog stays browsable.
          request(() => base44.entities.ViatorExperience.filter({ status: "Published" }, { sort: "-review_count", limit: 250 }), 15000, "Experience catalog query"),
          request(() => base44.entities.ViatorExperience.filter(
            { status: "Published", theme: "bad-bunny" },
            { sort: "-review_count", limit: 24 }
          ), 12000, "Special experience catalog query"),
          request(() => base44.entities.ViatorExperience.filter(
            { status: "Published", title: { $regex: "ransfer|ransport|huttle|axi|hauffeur|imousine|inibus" } },
            { sort: "-review_count", limit: 120 }
          ), 12000, "Transport catalog query"),
        ]).then(([viatorResult, bunnyResult, transferResult]) => {
          if (!alive) return;
          if (transferResult.status === "fulfilled") {
            setTransferCatalog((transferResult.value?.items || []).map(normalizeViator));
          }

          if (viatorResult.status === "fulfilled") {
            const firstPage = viatorResult.value;
            const collected = [...(firstPage?.items || [])];
            setViatorListings(rankPartner(collected.map(normalizeViator)));

            // Cursor pagination matters: more than a thousand Viator trips are
            // published, and the old 200-item cap silently hid most of them.
            // Keep the first page visible even if a subsequent request fails.
            if (firstPage?.has_more && firstPage?.next_cursor) {
              void (async () => {
                let cursor = firstPage.next_cursor;
                const usedCursors = new Set();
                let pages = 0;
                while (alive && cursor && !usedCursors.has(cursor) && pages < 20) {
                  usedCursors.add(cursor);
                  const page = await request(
                    () => base44.entities.ViatorExperience.filter(
                      { status: "Published" },
                      { sort: "-review_count", limit: 250, cursor }
                    ),
                    15000,
                    "Experience catalog continuation"
                  );
                  collected.push(...(page?.items || []));
                  cursor = page?.has_more ? page.next_cursor : null;
                  pages += 1;
                }
                if (!alive) return;
                const seen = new Set();
                const unique = collected.filter((record) => {
                  const key = record.product_code || record.id;
                  if (!key || seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                setViatorListings(rankPartner(unique.map(normalizeViator)));
              })().catch((error) => {
                console.warn("Additional Viator experiences could not be loaded:", error);
                if (alive) setLoadError(true);
              });
            }
          } else {
            setLoadError(true);
          }
          if (bunnyResult.status === "fulfilled") {
            setBadBunnyListings((bunnyResult.value?.items || []).map(normalizeViator));
          } else {
            setLoadError(true);
          }
        }).catch(() => {
          if (alive) setLoadError(true);
        });
      } catch (e) {
        // Keep the guide usable with its empty fallbacks, but let the interface
        // explain that data could not be loaded instead of implying no places exist.
        if (alive) setLoadError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const chips = useMemo(() => ["Explore", ...CATEGORIES], []);

  const nbNames = useMemo(() => {
    const map = {};
    neighborhoods.forEach((n) => {
      [n.id, n.slug, n.name].filter(Boolean).forEach((key) => {
        map[key] = n.name;
      });
    });
    return map;
  }, [neighborhoods]);

  const hasFilter =
    activeChip !== "Explore" ||
    filterCats.size > 0 ||
    filterSubs.size > 0 ||
    filterNb.size > 0 ||
    query.trim() !== "" ||
    nearMe;

  // The partner catalog is split: trips stay experiences, while the transfers,
  // shuttles and private drivers are the island's transportation services.
  const rideServices = useMemo(() => {
    // The partner list carries the same airport run more than once (one record
    // per vehicle or zone), so each named service appears a single time.
    const ids = new Set();
    const titles = new Set();
    return [...transferCatalog, ...viatorListings]
      .filter((item) => {
        const title = String(item?.name || "").trim().toLowerCase();
        if (!item?.id || ids.has(item.id) || titles.has(title) || !isRideService(item)) return false;
        ids.add(item.id);
        titles.add(title);
        return true;
      })
      .map(asRideService);
  }, [transferCatalog, viatorListings]);
  const tourExperiences = useMemo(
    () => viatorListings.filter((item) => !isRideService(item)),
    [viatorListings]
  );

  // Local places and bookable experiences are searched together, so one filter
  // covers everything the site offers.
  const pool = useMemo(() => {
    // A special-interest Viator feed may contain an experience already loaded
    // by the main catalog. Show it only once in browse and search.
    const seen = new Set();
    return [...allListings, ...tourExperiences, ...rideServices, ...badBunnyListings]
      .filter((item) => {
        if (!item?.id || seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
  }, [allListings, tourExperiences, rideServices, badBunnyListings]);

  // The zones the advanced search asked for, resolved to the neighborhood
  // records their ids, slugs and names come from.
  const pickedZones = useMemo(
    () => neighborhoods.filter((n) => filterNb.has(n.slug) || filterNb.has(n.id) || filterNb.has(n.name)),
    [neighborhoods, filterNb]
  );

  const filteredListings = useMemo(() => {
    const kept = photosFirst(mixSources(
      pool.filter((l) => {
        if (query.trim()) {
          const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);
          const nb = neighborhoods.find((n) => n.slug === l.neighborhood_id || n.id === l.neighborhood_id);
          const hay = [
            l.name, l.category, l.subcategory, l.type,
            l.editorial_description, l.description, l.short_description, l.address,
            l.price_label, l.hours_text, l.language_support, l.tags,
            nb?.name, nb?.slug,
            ...(l.amenities || []),
          ].filter(Boolean).flat().map(normalizeSearchText).join(" ");
          // Every normalized token must match somewhere in the combined haystack.
          if (!tokens.every((t) => hay.includes(t))) return false;
        }
        const cats = new Set([...(activeChip !== "Explore" ? [activeChip] : []), ...filterCats]);
        if (cats.size) {
          let ok = false;
          for (const c of cats) if (catMatches(l, c)) { ok = true; break; }
          if (!ok) return false;
        }
        if (filterSubs.size && !Array.from(filterSubs).some((s) => subMatches(l, s))) return false;
          // A zone is matched on the neighborhood's id, its slug, or the words
          // the listing was stored with — the directory holds all three.
          if (filterNb.size && !pickedZones.some((z) => zoneMatches(l, z))) return false;
          return true;
        })
    ));
    // "Near Me" takes nothing away — it leads with the closest places, so the
    // nearest of the catalog heads whatever else the browse is showing.
    if (!nearMe || !nearCoords) return kept;
    return [...kept].sort((a, b) => distanceKm(a, nearCoords) - distanceKm(b, nearCoords));
  }, [pool, query, activeChip, filterCats, filterSubs, filterNb, pickedZones, nearMe, nearCoords]);

  const clearFilters = useCallback(() => {
    setActiveChip("Explore");
    setExperienceBucket("All");
    setFilterCats(new Set());
    setFilterSubs(new Set());
    setFilterNb(new Set());
    setQuery("");
    setNearMe(false);
  }, []);

  // One tap on "Near Me": ask the browser where the visitor is — or reuse the
  // position they already gave this session — and lead the browse with it.
  // A refusal is reported, never fatal: the guide browses on without it.
  const toggleNearMe = useCallback(() => {
    if (nearMe) {
      setNearMe(false);
      setNearError("");
      return;
    }
    if (nearCoords) {
      setNearMe(true);
      return;
    }
    if (!navigator.geolocation) {
      setNearError("unavailable");
      return;
    }
    setLocating(true);
    setNearError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setNearCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setNearMe(true);
      },
      () => {
        setLocating(false);
        setNearError("blocked");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [nearMe, nearCoords]);

  // A visitor who already let the browser share their position gets the nearby
  // order without tapping anything: the guide leads with what is closest as
  // soon as it loads, and never asks for permission a second time.
  useEffect(() => {
    if (nearCoords || !navigator.geolocation) return undefined;
    let live = true;

    const watchForGrant = async () => {
      try {
        const status = await navigator.permissions?.query({ name: "geolocation" });
        if (!live || status?.state !== "granted") return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!live) return;
            setNearCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
            setNearMe(true);
          },
          () => {},
          { timeout: 8000, maximumAge: 300000 }
        );
      } catch {
        // No permission API on this browser: Near Me still works on a tap.
      }
    };
    watchForGrant();

    return () => {
      live = false;
    };
  }, [nearCoords]);

  const selectChip = useCallback((chip) => {
    if (activeChip === chip) {
      setDrawerOpen((o) => !o);
    } else {
      setActiveChip(chip);
      setDrawerOpen(true);
    }
  }, [activeChip]);

  const selectNeighborhood = useCallback((slug) => {
    setFilterNb(slug ? new Set([slug]) : new Set());
  }, []);

  const toggleSub = useCallback((sub) => {
    setFilterSubs((prev) => {
      const next = new Set(prev);
      next.has(sub) ? next.delete(sub) : next.add(sub);
      return next;
    });
  }, []);

  const scrollToExperiences = () =>
    setTimeout(() => {
      const target = document.querySelector("#category-results") || document.querySelector("#experiences");
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

  const onPick = (q) => {
    setQuery(q);
    setSearchOpen(false);
    navigate("/");
    scrollToExperiences();
  };
  const onApply = (cats, subs, nb) => {
    // The drawer's category takes the place of the browse chip: one category is
    // browsed at a time, so a chip left on could only union with what was just
    // chosen here. "Any category" clears it instead.
    setActiveChip(cats.size ? Array.from(cats)[0] : "Explore");
    setFilterCats(new Set());
    setFilterSubs(subs);
    setFilterNb(nb);
    setFilterOpen(false);
    navigate("/");
    scrollToExperiences();
  };

  // The drawer opens on what is already being browsed: the active chip is a
  // category, and its own types are then exposed without another tap.
  const seedCategory = CATEGORIES.includes(activeChip) ? activeChip : Array.from(filterCats)[0] || "";

  const value = {
    allListings, beaches, viatorListings, tourExperiences, rideServices, badBunnyListings, neighborhoods, categories, chips, loading, loadError, nbNames,
    query, setQuery, activeChip, setActiveChip, experienceBucket, setExperienceBucket,
    filterCats, setFilterCats, filterSubs, setFilterSubs, filterNb, setFilterNb,
    clearFilters, filteredListings, hasFilter,
    nearMe, setNearMe, nearCoords, locating, nearError, toggleNearMe,
    searchOpen, setSearchOpen, filterOpen, setFilterOpen,
    drawerOpen, setDrawerOpen, selectChip, selectNeighborhood, toggleSub,
  };

  return (
    <ExploreContext.Provider value={value}>
      {children}
      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        neighborhoods={neighborhoods.map((n) => ({ name: n.name, slug: n.slug }))}
        onApply={onApply}
        category={seedCategory}
        subs={filterSubs}
        nb={Array.from(filterNb)[0] || ""}
      />
    </ExploreContext.Provider>
  );
}

export const useExplore = () => useContext(ExploreContext);